
import React from 'react';
import { motion } from 'framer-motion';
import { CHAT_ANIMATIONS } from '@/utils/chat-animations';
import { CopilotAssistant } from '@/hooks/ai-agents/types';
import { MessageDisplay } from '@/hooks/ai-agents/types/chat-hook-types';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import MessageContent from './MessageContent';
import { ChatRecommendationsDisplay } from './functions/ChatRecommendationsDisplay';
import { CopyMessageButton } from './functions/CopyMessageButton';
import { OptimizedImage } from '@/components/ui/OptimizedImage/OptimizedImage';
import { useUser } from '@/hooks/auth/use-user';
import { useProfileData } from '@/hooks/profile';
import { useRecommendationDetection } from '@/hooks/ai-agents/functions/core/useRecommendationDetection';

interface MessageBubbleProps {
  message: MessageDisplay;
  activeAssistant: CopilotAssistant | null;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
}

// Use centralized message animation variants
const messageVariants = CHAT_ANIMATIONS.messageEntry;

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  activeAssistant,
  isFirstInGroup = false,
  isLastInGroup = false
}) => {
  const isAssistant = message.role === 'assistant';
  const timestamp = message.createdAt ? new Date(message.createdAt) : new Date();
  
  // Get user data from auth hook
  const { user } = useUser();
  
  // Get user profile data using the hook that's used in the account dropdown
  // We need to pass the user from useUser() to the useProfileData hook
  const { profileData } = useProfileData();

  // Determine which assistant to use for display
  // Priority: message's assistant metadata, then activeAssistant fallback
  const displayAssistant = message.assistant || activeAssistant;

  // ✅ RECOMMENDATION DETECTION: Check if this message will show recommendations
  const { hasRecommendations } = useRecommendationDetection(
    isAssistant ? (message.content || '') : ''
  );
  
  // Determine if recommendations will be shown
  const willShowRecommendations = isAssistant && hasRecommendations;
  
  // Parse content to extract only conversational part for display
  const getConversationalContent = (content: string) => {
    if (content.includes('---RECOMMENDATION---')) {
      // Extract everything before the recommendation delimiter
      return content.split('---RECOMMENDATION---')[0].trim();
    }
    return content;
  };
  
  // Use parsed content for assistant messages, full content for user messages
  const displayContent = isAssistant ? getConversationalContent(message.content || '') : message.content;

  return (
    <motion.div
      variants={messageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      layout
      className={cn(
        "flex gap-3",
        isAssistant ? "justify-start" : "justify-end"
      )}
      data-testid="message-bubble"
    >
      <div className={cn(
        "flex flex-col max-w-[95%] md:max-w-[95%]",
        !isAssistant && "items-end"
      )}>
        {isFirstInGroup && isAssistant && displayAssistant && (
          <span className="text-xs text-slate-500 dark:text-slate-400 ml-1 mb-1">
            {displayAssistant.name || 'Assistant'}
          </span>
        )}
        
        {/* Always show message content, regardless of recommendations */}
        {displayContent && (
          <motion.div
            className={cn(
              "px-4 py-2.5 rounded-2xl shadow-sm",
              isAssistant ? 
                "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700/50" : 
                "bg-blue-100 dark:bg-blue-900/30 border border-blue-300 dark:border-blue-700/50 text-black dark:text-blue-100",
              isFirstInGroup && isAssistant ? "rounded-tl-md" : "",
              !isLastInGroup && isAssistant ? "rounded-bl-md" : "",
              isFirstInGroup && !isAssistant ? "rounded-tr-md" : "",
              !isLastInGroup && !isAssistant ? "rounded-br-md" : ""
            )}
          >
            <MessageContent content={displayContent} isAssistant={isAssistant} />
          </motion.div>
        )}
        
        {/* Recommendations Display - positioned below message or replaces message */}
        {isAssistant && (
          <ChatRecommendationsDisplay message={message} />
        )}
        
        <div className={cn(
          "text-[10px] text-slate-400 dark:text-slate-500 px-1 mt-2",
          isAssistant ? "flex items-center justify-between" : "flex items-center justify-end"
        )}>
          {isAssistant && (
            <>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 mr-1 rounded-full overflow-hidden bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                  {displayAssistant?.profile_picture_url ? (
                    <OptimizedImage 
                      src={displayAssistant.profile_picture_url}
                      alt={displayAssistant.name}
                      className="h-4 w-4 object-cover"
                      onError={(e) => {
                        // Fallback to initials if image fails to load
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `<span class="text-[6px] text-blue-600 dark:text-blue-400 font-bold">${displayAssistant?.name?.substring(0, 2) || 'AI'}</span>`;
                        }
                      }}
                    />
                  ) : (
                    <span className="text-[6px] text-blue-600 dark:text-blue-400 font-bold">
                      {displayAssistant?.name?.substring(0, 2) || 'AI'}
                    </span>
                  )}
                </div>
                <span>{format(timestamp, 'h:mm a')}</span>
                

              </div>
              
              {/* Show copy button when there's content to copy */}
              {displayContent && (
                <CopyMessageButton message={{ ...message, content: displayContent }} />
              )}
            </>
          )}
          
          {!isAssistant && (
            <>
              <span>{format(timestamp, 'h:mm a')}</span>
              <div className="h-4 w-4 ml-1 rounded-full overflow-hidden bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                {profileData?.profilePictureUrl ? (
                  <OptimizedImage 
                    src={profileData.profilePictureUrl}
                    alt="You"
                    className="h-4 w-4 object-cover"
                    onError={(e) => {
                      // Fallback to "You" text if profile image fails to load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = '<span class="text-[6px] text-blue-600 dark:text-blue-400 font-bold">You</span>';
                      }
                    }}
                  />
                ) : (
                  <span className="text-[6px] text-blue-600 dark:text-blue-400 font-bold">
                    You
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default MessageBubble;
